/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { HTMLAttributes, useState, useEffect } from 'preact/compat';
import { Container } from '@adobe-commerce/elsie/lib';
import { Coupons as CouponsComponent } from '@/cart/components';
import { Button, Input, Icon, Tag } from '@adobe-commerce/elsie/components';
import { Close, GiftCard } from '@adobe-commerce/elsie/icons';
import { applyGiftCardToCart, removeGiftCardFromCart } from '@/cart/api';
import { events } from '@adobe-commerce/event-bus';
import { useText } from '@adobe-commerce/elsie/i18n';

export interface GiftCardsProps extends HTMLAttributes<HTMLDivElement> {}

export const GiftCards: Container<GiftCardsProps> = ({
  children,
  ...props
}) => {
  const [successfulGiftCardSet, setSuccessfulGiftCardSet] = useState(new Set());
  const [appliedGiftCardsArray, setAppliedGiftCardsArray] = useState<string[]>(
    []
  );
  const [giftCardError, setGiftCardError] = useState(new Set());

  const dictionary = useText({
    giftCardTitle: 'Cart.PriceSummary.giftCard.title',
    applyButton: 'Cart.PriceSummary.giftCard.applyAction',
    applyButtonAriaLabel: 'Cart.PriceSummary.giftCard.applyActionAriaLabel',
    placeholder: 'Cart.PriceSummary.giftCard.placeholder',
    ariaLabel: 'Cart.PriceSummary.giftCard.ariaLabel',
    ariaLabelRemove: 'Cart.PriceSummary.giftCard.ariaLabelRemove',
    empty: 'Cart.PriceSummary.giftCard.errors.empty',
  });

  const handleApplyGiftCard = async (value: { giftCardCode: string }) => {
    const giftCardCode = value?.giftCardCode;

    if (giftCardCode === '') {
      setGiftCardError(new Set([dictionary.empty]));
      return true;
    }

    const newSet = new Set(successfulGiftCardSet);
    newSet.add(giftCardCode);
    setGiftCardError(new Set());

    applyGiftCardToCart(giftCardCode)
      .then((result) => {
        if (result === null) {
          throw new Error('Error adding gift card code');
        }

        setSuccessfulGiftCardSet(newSet);
      })
      .catch((error) => {
        console.warn(error);
        setGiftCardError(new Set([error.message]));
      });
  };

  const handleRemoveGiftCard = (giftCardCode: string) => {
    const newSet = new Set(successfulGiftCardSet);
    newSet.delete(giftCardCode);
    setGiftCardError(new Set());

    removeGiftCardFromCart(giftCardCode)
      .then((result) => {
        if (result === null) {
          throw new Error('Error removing gift card code');
        }

        setSuccessfulGiftCardSet(newSet);
      })
      .catch((error) => {
        console.warn(error);
        setGiftCardError(new Set([error.message]));
      });
  };

  useEffect(() => {
    const cartEvent = events.on(
      'cart/data',
      (payload) => {
        const appliedGiftCards = payload?.appliedGiftCards;

        if (!appliedGiftCards) {
          // When appliedGiftCards returns null, no GiftCards are applied
          setAppliedGiftCardsArray([]);
          // Clear error message
          setGiftCardError(new Set());
          return;
        }

        const giftCardCodes = appliedGiftCards.map(({ code }) => code);

        setAppliedGiftCardsArray(giftCardCodes);
        // Clear error message
        setGiftCardError(new Set());
      },
      { eager: true }
    );

    return () => {
      cartEvent?.off();
    };
  }, []);

  useEffect(() => {
    setSuccessfulGiftCardSet(new Set(appliedGiftCardsArray));
  }, [appliedGiftCardsArray]);

  const appliedGiftCards = appliedGiftCardsArray.map((code, index) => (
    <Tag key={code} className="coupon-code-form__applied-item">
      <span>{code}</span>
      <button
        aria-label={`${dictionary.ariaLabelRemove} ${code}`}
        onClick={() => handleRemoveGiftCard(code)}
        data-testid={`remove-giftcard-${index + 1}`}
      >
        <Icon source={Close} size={'16'} />
      </button>
    </Tag>
  ));

  const errorMessage =
    giftCardError.size > 0 ? (
      <div data-testid="giftcard-code-error">
        {Array.from(giftCardError)[0] as string}
      </div>
    ) : undefined;

  return (
    <CouponsComponent
      {...props}
      className={'cart-gift-cards'}
      accordionSectionTitle={dictionary.giftCardTitle}
      accordionSectionIcon={GiftCard}
      couponCodeField={
        <Input
          aria-label={dictionary.ariaLabel}
          type="text"
          placeholder={dictionary.placeholder}
          name="giftCardCode"
          variant="primary"
          value=""
          data-testid="giftcard-code-input"
          maxLength={50}
          error={giftCardError.size > 0}
        />
      }
      applyCouponsButton={
        <Button
          variant="secondary"
          aria-label={dictionary.applyButtonAriaLabel}
        >
          {dictionary.applyButton}
        </Button>
      }
      error={errorMessage}
      appliedCoupons={<div>{appliedGiftCards}</div>}
      onApplyCoupon={handleApplyGiftCard}
    />
  );
};
